<template>
  <div id="dialog-giftshowList">
    <div class="dialog">
      <span class="cls" @click="oth_viewList(false)"></span>
      <div class="paper">
        <!-- 헤더 -->
        <div class="header">
          <h2>🎁 선물하기</h2>
          <button class="close-btn" @click="oth_viewList(false)">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>

        <!-- 필터 & 검색 -->
        <div class="filter-bar">
          <div class="search-box">
            <i class="ph-bold ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchKeyword" 
              placeholder="상품명 검색"
              @input="onSearch"
            />
          </div>
          <select v-model="selectedCategory" @change="filterGoods">
            <option value="">전체 카테고리</option>
            <option v-for="cat in categories" :key="cat.seq" :value="cat.seq">
              {{ cat.name }}
            </option>
          </select>
          <select v-model="selectedBrand" @change="filterGoods">
            <option value="">전체 브랜드</option>
            <option v-for="brand in filteredBrands" :key="brand.code" :value="brand.code">
              {{ brand.name }}
            </option>
          </select>
        </div>

        <!-- 상품 리스트 -->
        <div class="content" ref="listContainer">
          <div v-if="loading && filteredList.length === 0" class="loading">
            <i class="ph-bold ph-spinner"></i>
            <span>불러오는 중...</span>
          </div>

          <div v-else-if="filteredList.length === 0" class="empty">
            <i class="ph-bold ph-storefront"></i>
            <span>상품이 없습니다</span>
          </div>

          <ul v-else class="goods-grid">
            <li 
              v-for="item in filteredList" 
              :key="item.goodsCode"
              @click="openDetail(item.goodsCode)"
            >
              <div class="img-wrap">
                <img class="goods-img" :src="item.goodsImgB" :alt="item.goodsName" />
                <img class="brand-img" :src="item.brandIconImg" :alt="item.brandName" />
                <span v-if="item.discountRate > 0" class="discount-badge">
                  {{ item.discountRate }}%
                </span>
              </div>
              <div class="info">
                <div class="info-top">
                  <span class="brand">{{ item.brandName }}</span>
                  <span class="goods-code" @click.stop="copyCode(item.goodsCode)">
                    {{ item.goodsCode }}
                    <i class="ph ph-copy-simple"></i>
                  </span>
                </div>
                <p class="name">{{ item.goodsName }}</p>
                <div class="price">
                  <strong>{{ formatPrice(item.discountPrice) }}원</strong>
                  <del v-if="item.discountPrice < item.realPrice">
                    {{ formatPrice(item.realPrice) }}원
                  </del>
                </div>
              </div>
            </li>
          </ul>

          <div v-if="loading && filteredList.length > 0" class="loading-more">
            <i class="ph-bold ph-spinner"></i>
          </div>
        </div>

        <!-- 페이지네이션 -->
        <div class="pagination" v-if="totalPages > 0">
          <button 
            class="page-btn" 
            :disabled="currentPage === 1"
            @click="goToPage(1)"
          >처음</button>
          <button 
            class="page-btn" 
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            <i class="ph-bold ph-caret-left"></i>
          </button>
          
          <template v-for="page in visiblePages" :key="page">
            <span v-if="page === '...'" class="ellipsis">...</span>
            <button 
              v-else
              class="page-btn"
              :class="{ active: page === currentPage }"
              @click="goToPage(page)"
            >{{ page }}</button>
          </template>
          
          <button 
            class="page-btn" 
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            <i class="ph-bold ph-caret-right"></i>
          </button>
          <button 
            class="page-btn" 
            :disabled="currentPage === totalPages"
            @click="goToPage(totalPages)"
          >맨뒤</button>
        </div>
      </div>
    </div>

    <!-- 상품 상세 다이얼로그 -->
    <Teleport to="body">
      <dialog_giftshowDetail 
        v-if="detailShow" 
        :goods_code="selectedGoodsCode" 
        :oth_viewDetail="closeDetail" 
      />
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';
import dialog_giftshowDetail from './giftshowDetail.vue';

export default {
  name: 'giftshowList',
  components: { dialog_giftshowDetail },
  props: {
    oth_viewList: {
      type: Function,
      required: true
    }
  },
  setup() {
    const getGiftishowGoods = inject('getGiftishowGoods');

    const loading = ref(false);
    const goodsList = ref([]);
    const searchKeyword = ref('');
    const selectedCategory = ref('');
    const selectedBrand = ref('');

    // 페이지네이션
    const currentPage = ref(1);
    const pageSize = ref(20);
    const totalCount = ref(0);

    const totalPages = computed(() => {
      return Math.ceil(totalCount.value / pageSize.value) || 1;
    });

    // 보여줄 페이지 번호 (처음 < 1 2 3 4 5 ... > 맨뒤)
    const visiblePages = computed(() => {
      const total = totalPages.value;
      const current = currentPage.value;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    });

    // 상세 다이얼로그
    const detailShow = ref(false);
    const selectedGoodsCode = ref('');

    // 카테고리 (하드코딩 - API에서 별도 제공 안함)
    const categories = ref([
      { seq: 4, name: '편의점/마트' },
      { seq: 5, name: '피자/버거/치킨' },
      { seq: 2, name: '카페/베이커리' },
      { seq: 6, name: '외식/배달' },
      { seq: 7, name: '뷰티/생활' },
      { seq: 8, name: '상품권' },
    ]);

    // 브랜드 (상품에서 추출)
    const brands = computed(() => {
      const map = new Map();
      goodsList.value.forEach(item => {
        if (item.brandCode && !map.has(item.brandCode)) {
          map.set(item.brandCode, {
            code: item.brandCode,
            name: item.brandName,
            category: item.category1Seq
          });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    });

    // 선택된 카테고리에 맞는 브랜드만
    const filteredBrands = computed(() => {
      if (!selectedCategory.value) return brands.value;
      return brands.value.filter(b => b.category === selectedCategory.value);
    });

    // 필터링된 상품 리스트
    const filteredList = computed(() => {
      let list = goodsList.value;

      // 카테고리 필터
      if (selectedCategory.value) {
        list = list.filter(item => item.category1Seq === selectedCategory.value);
      }

      // 브랜드 필터
      if (selectedBrand.value) {
        list = list.filter(item => item.brandCode === selectedBrand.value);
      }

      // 검색어 필터
      if (searchKeyword.value.trim()) {
        const keyword = searchKeyword.value.toLowerCase();
        list = list.filter(item => 
          item.goodsName.toLowerCase().includes(keyword) ||
          item.brandName.toLowerCase().includes(keyword)
        );
      }

      return list;
    });

    const formatPrice = (price) => {
      return price?.toLocaleString() || '0';
    };

    // 상품 로드
    // fetchGoods 수정
    async function fetchGoods(page = 1) {
      if (loading.value) return;
      loading.value = true;

      try {
        const res = await getGiftishowGoods(page, pageSize.value);
        if (res?.result) {
          goodsList.value = res.result.goodsList?.filter(g => g.goodsStateCd === 'SALE') || [];
          totalCount.value = res.result.listNum || 0;  // listNum으로 변경
          currentPage.value = page;
        }
      } catch (e) {
        console.error('상품 로드 실패:', e);
      } finally {
        loading.value = false;
      }
    }
    function goToPage(page) {
      if (page < 1 || page > totalPages.value || page === currentPage.value) return;
      fetchGoods(page);
      
      // 스크롤 맨 위로
      const container = document.querySelector('#dialog-giftshowList .content');
      if (container) container.scrollTop = 0;
    }

    function filterGoods() {
      // 카테고리 변경시 브랜드 초기화
      if (selectedCategory.value) {
        const validBrands = filteredBrands.value.map(b => b.code);
        if (!validBrands.includes(selectedBrand.value)) {
          selectedBrand.value = '';
        }
      }
    }

    let searchTimeout = null;
    function onSearch() {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        // 검색 시 자동 필터링 (computed가 처리)
      }, 300);
    }

    function openDetail(code) {
      selectedGoodsCode.value = code;
      detailShow.value = true;
    }

    function closeDetail() {
      detailShow.value = false;
      selectedGoodsCode.value = '';
    }

    function copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        alert(`상품코드 ${code} 복사됨`);
      }).catch(() => {
        prompt('상품코드:', code);
      });
    }

    onMounted(() => {
      fetchGoods(1);
    });

    return {
      loading,
      goodsList,
      searchKeyword,
      selectedCategory,
      selectedBrand,
      categories,
      brands,
      filteredBrands,
      filteredList,
      formatPrice,
      filterGoods,
      onSearch,
      detailShow,
      selectedGoodsCode,
      openDetail,
      closeDetail,
      copyCode,
      // 페이지네이션
      currentPage,
      totalPages,
      visiblePages,
      goToPage
    };
  }
}
</script>

<style scoped>
#dialog-giftshowList {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
}

.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(4px);
}

.cls {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
}

.paper {
  width: 90%;
  max-width: 800px;
  height: 85vh;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.close-btn {
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-surface);
}

/* 필터 바 */
.filter-bar {
  display: flex;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--color-border);
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 150px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.search-box i {
  color: var(--color-text-tertiary);
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.filter-bar select {
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
}

/* 컨텐츠 */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* 로딩 */
.loading, .empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: var(--color-text-tertiary);
}

.loading i, .loading-more i {
  font-size: 24px;
  animation: spin 1s linear infinite;
}

.loading-more {
  display: flex;
  justify-content: center;
  padding: 20px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 상품 그리드 */
.goods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.goods-grid li {
  cursor: pointer;
  border-radius: var(--radius-lg);
  transition: transform 0.2s, box-shadow 0.2s;
}

.goods-grid li:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.img-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--color-surface);
}

.goods-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.brand-img {
  position: absolute;
  left: 8px;
  top: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.discount-badge {
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 4px 8px;
  background: var(--color-danger);
  color: #fff;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-sm);
}

.info {
  padding: 10px 4px;
}

.info-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info .brand {
  font-size: 11px;
  color: var(--color-text-tertiary);
}

.goods-code {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: var(--color-text-quaternary);
  font-family: monospace;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 4px;
  transition: all 0.15s;
}

.goods-code i {
  font-size: 10px;
}

.goods-code:hover {
  background: var(--color-surface);
  color: var(--color-brand);
}

.info .name {
  margin: 4px 0 6px;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.info .price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-wrap: wrap;
}

.info .price strong {
  font-size: 14px;
  color: var(--color-text-primary);
}

.info .price del {
  font-size: 11px;
  color: var(--color-text-quaternary);
}

/* 페이지네이션 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: var(--color-background);
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

.ellipsis {
  color: var(--color-text-tertiary);
  padding: 0 4px;
}

/* 모바일 */
@media (max-width: 600px) {
  .paper {
    width: 100%;
    height: 100%;
    max-width: 100%;
    border-radius: 0;
  }

  .filter-bar {
    flex-direction: column;
  }

  .filter-bar select {
    width: 100%;
  }

  .goods-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .pagination {
    gap: 4px;
    padding: 12px 16px;
  }

  .page-btn {
    min-width: 32px;
    height: 32px;
    font-size: 12px;
  }
}
</style>